import {Injectable} from '@angular/core';
import {Product} from '../../models/product';
import {OrderItem} from '../../models/order-item';
import {environment} from '../../environments/environment.production';

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
    }
}

interface Ga4Item {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
}

/**
 * Google Analytics 4 e-commerce events: page_view -> view_item -> add_to_cart ->
 * begin_checkout -> add_shipping_info -> purchase, which GA4 turns into the purchase funnel
 * and revenue per traffic source. gtag.js itself is loaded in index.html.
 *
 * Every call is wrapped: an ad blocker or a missing tag must never break the shop.
 */
@Injectable({providedIn: 'root'})
export class AnalyticsService {

    private configured = false;

    /** Call once at startup. The app is a SPA, so page views are sent on route changes instead. */
    init(): void {
        if (this.configured || !environment.ga4MeasurementId) {
            return;
        }
        this.gtag('config', environment.ga4MeasurementId, {send_page_view: false});
        this.configured = true;
    }

    pageView(title: string): void {
        this.event('page_view', {
            page_title: title,
            page_location: window.location.href,
            page_path: window.location.pathname
        });
    }

    viewItem(product: Product): void {
        this.event('view_item', {
            currency: 'MKD',
            value: product.price,
            items: [this.item(product, 1)]
        });
    }

    addToCart(product: Product, quantity: number): void {
        this.event('add_to_cart', {
            currency: 'MKD',
            value: product.price * quantity,
            items: [this.item(product, quantity)]
        });
    }

    beginCheckout(value: number, orderItems: OrderItem[]): void {
        this.event('begin_checkout', {currency: 'MKD', value, items: this.items(orderItems)});
    }

    addShippingInfo(value: number, orderItems: OrderItem[]): void {
        this.event('add_shipping_info', {
            currency: 'MKD',
            value,
            shipping_tier: 'cash_on_delivery',
            items: this.items(orderItems)
        });
    }

    /**
     * The order, for GA4 revenue and - when a conversion action is set up - for Google Ads.
     * transaction_id lets both drop a duplicate if the event is ever sent twice.
     */
    purchase(orderId: string, value: number, orderItems: OrderItem[]): void {
        this.event('purchase', {
            transaction_id: orderId,
            currency: 'MKD',
            value,
            items: this.items(orderItems)
        });
        const ads = environment.googleAds;
        if (ads.id && ads.purchaseLabel) {
            this.gtag('event', 'conversion', {
                send_to: `${ads.id}/${ads.purchaseLabel}`,
                transaction_id: orderId,
                currency: 'MKD',
                value
            });
        }
    }

    private items(orderItems: OrderItem[]): Ga4Item[] {
        return orderItems
            .filter(orderItem => orderItem.product)
            .map(orderItem => this.item(orderItem.product!, orderItem.quantity ?? 1));
    }

    private item(product: Product, quantity: number): Ga4Item {
        return {
            item_id: String(product.id),
            item_name: product.name,
            item_category: product.category,
            price: product.price,
            quantity
        };
    }

    private event(name: string, params: Record<string, unknown>): void {
        if (!environment.ga4MeasurementId) {
            return;
        }
        this.gtag('event', name, {...params, send_to: environment.ga4MeasurementId});
    }

    private gtag(...args: unknown[]): void {
        try {
            window.gtag?.(...args);
        } catch {
            // gtag blocked / not available
        }
    }
}
